import { useState, Suspense, lazy, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import { CartProvider } from './context/CartContext'
import { ToastProvider } from './components/Toast'
import ErrorBoundary from './components/ErrorBoundary'
import PageTransition from './components/PageTransition'
import AuthModal from './components/AuthModal'
import Cart from './components/Cart'

// Eagerly loaded pages (small, frequently visited)
import Home from './pages/Home'
import About from './pages/About'
import Shop from './pages/Shop'
import Events from './pages/Events'

// Lazy loaded pages (larger bundles or less frequently visited)
const Magazine = lazy(() => import('./pages/Magazine'))
const EventDetail = lazy(() => import('./pages/EventDetail'))
const YamkaGames = lazy(() => import('./pages/YamkaGames'))
const WatchClub = lazy(() => import('./pages/WatchClub'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Stats = lazy(() => import('./pages/Stats'))
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'))
const CheckoutCancel = lazy(() => import('./pages/CheckoutCancel'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogPost = lazy(() => import('./pages/BlogPost'))

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <svg className="w-8 h-8 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    </div>
  )
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function AppRoutes({ openAuthModal }) {
  const location = useLocation()

  return (
    <PageTransition key={location.pathname}>
      <Suspense fallback={<PageLoader />}>
        <Routes location={location}>
          <Route path="/" element={<Home onLoginClick={openAuthModal} />} />
          <Route path="/about" element={<About onLoginClick={openAuthModal} />} />
          <Route path="/shop" element={<Shop onLoginClick={openAuthModal} />} />
          <Route path="/magazine" element={<Magazine onLoginClick={openAuthModal} />} />
          <Route path="/events" element={<Events onLoginClick={openAuthModal} />} />
          <Route path="/events/yamka-games" element={<YamkaGames onLoginClick={openAuthModal} />} />
          <Route path="/events/watch-club" element={<WatchClub onLoginClick={openAuthModal} />} />
          <Route path="/events/:eventId" element={<EventDetail onLoginClick={openAuthModal} />} />
          <Route path="/dashboard" element={<Dashboard onLoginClick={openAuthModal} />} />
          <Route path="/stats" element={<Stats onLoginClick={openAuthModal} />} />
          <Route path="/checkout/success" element={<CheckoutSuccess onLoginClick={openAuthModal} />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel onLoginClick={openAuthModal} />} />
          <Route path="/blog" element={<Blog onLoginClick={openAuthModal} />} />
          <Route path="/blog/:slug" element={<BlogPost onLoginClick={openAuthModal} />} />
        </Routes>
      </Suspense>
    </PageTransition>
  )
}

function App() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)

  const openAuthModal = () => setIsAuthModalOpen(true)
  const closeAuthModal = () => setIsAuthModalOpen(false)

  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <ToastProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AppRoutes openAuthModal={openAuthModal} />
              <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
              <Cart />
            </BrowserRouter>
          </ToastProvider>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
